import { prettifyDisplay } from '@/common/utils';

/** Everything that decides whether, and where, we ask someone for their name.
 *  One module so the login step and the route gate can never disagree. */

export interface NameHolder {
  first_name?: string | null;
  last_name?: string | null;
  display?: string | null;
  email?: string | null;
}

export type PrefillSource = 'idp' | 'suggestion' | 'none';

export interface NameSuggestion {
  first: string;
  last: string;
  source: PrefillSource;
}

const DISMISSED_KEY = 'r41_profile_name_prompt_dismissed';

/** Both halves are required, so a first-name-only record still counts as
 *  needing a name — that includes everyone the SSO backfill could only fill
 *  halfway, because their provider sent no family name. Someone with a single
 *  name is not trapped: the prompt is skippable everywhere it appears. */
export function needsName(user: NameHolder | null | undefined): boolean {
  if (!user) return false;
  return !user.first_name?.trim() || !user.last_name?.trim();
}

/** What to put in the inputs before the person types.
 *
 *  A `suggestion` is a guess off the email and is wrong far more often than it
 *  is right, so callers must render it as editable text and label it as a
 *  guess. It is never saved on the person's behalf.
 */
export function nameSuggestion(user: NameHolder | null | undefined): NameSuggestion {
  const first = user?.first_name?.trim() ?? '';
  const last = user?.last_name?.trim() ?? '';
  if (first || last) return { first, last, source: 'idp' };

  const guess = prettifyDisplay(user?.email);
  if (!guess) return { first: '', last: '', source: 'none' };

  const [guessedFirst, ...rest] = guess.split(' ');
  return { first: guessedFirst, last: rest.join(' '), source: 'suggestion' };
}

/** Routes that own the whole viewport for something the person cannot redo.
 *  Interrupting a live interview or a timed assessment to ask for a name costs
 *  an interview; the name can wait. */
const NEVER_INTERRUPT = ['/interview/', '/coding-assessment/'];

/** Surfaces where a full-screen overlay would land on top of something already
 *  moving or already one-purpose: the lobby runs a countdown that can navigate
 *  on its own, and the booking and desktop-handoff pages are single-action
 *  screens reached from an email link. */
const NO_INTERSTITIAL = ['/job/', '/booking', '/desktop-auth'];

const startsWithAny = (pathname: string, prefixes: string[]): boolean =>
  prefixes.some((prefix) => pathname.startsWith(prefix));

export function allowsLoginStep(pathname: string): boolean {
  return !startsWithAny(pathname, NEVER_INTERRUPT);
}

export function allowsInterstitial(pathname: string): boolean {
  return !startsWithAny(pathname, [...NEVER_INTERRUPT, ...NO_INTERSTITIAL]);
}

/** Dismissal lives in sessionStorage, not localStorage, and that is the whole
 *  mechanism behind asking again on the next sign-in: a skip holds for the rest
 *  of this tab session, so nobody is nagged on every navigation, but it is gone
 *  by the time they next log in. */
export function markPromptDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    // Private browsing and blocked storage: worst case we ask again this session.
  }
}

export function isPromptDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}
